import React, { useState } from 'react';
import { Eye, EyeOff, User, Phone, Mail, Lock, Sparkles, CheckCircle, Store, Zap, Shield, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import UniversalNavbar from './UniversalNavbar';
import VFooter from './VFooter';
import Button from './ui/Button';
import Input from './ui/Input';
import Alert from './ui/Alert';
import COLORS from '../utils/colors';
import API_BASE_URL from '../config/api';


import { devLog } from '../utils/devLog';
const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    contact_number: '',
    email: '',
    password: '',
    confirm_password: '',
    termsAccepted: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentSection, setCurrentSection] = useState(1);

  // Sanitize brand username as the user types: lowercase, allow a-z, 0-9 and dashes only.
  const sanitizeSlug = (v) =>
    v
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-{2,}/g, '-')
      .slice(0, 30);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue =
      type === 'checkbox' ? checked : name === 'username' ? sanitizeSlug(value) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    // Clear field-level error as user edits
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errs = {};
    const { username, email, contact_number, password, confirm_password, termsAccepted } = formData;

    if (!username || username.length < 3) {
      errs.username = 'Brand username must be at least 3 characters (letters, numbers, dashes).';
    } else if (!/^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/.test(username)) {
      errs.username = 'Use lowercase letters, numbers, and dashes only. No spaces or special characters.';
    }

    if (!email) {
      errs.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = 'Please enter a valid email address.';
    }

    if (!contact_number) {
      errs.contact_number = 'Contact number is required.';
    } else if (!/^\d{10}$/.test(contact_number)) {
      errs.contact_number = 'Phone number must be exactly 10 digits.';
    }

    if (!password || password.length < 8) {
      errs.password = 'Password must be at least 8 characters.';
    }

    if (password !== confirm_password) {
      errs.confirm_password = 'Passwords do not match.';
    }

    if (!termsAccepted) {
      errs.termsAccepted = 'Please accept the terms and conditions.';
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      // Scroll the first invalid field into view
      const firstErrorField = Object.keys(fieldErrors)[0] || 'username';
      const el = document.getElementById(firstErrorField);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setErrorMessage('Please fix the highlighted fields.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Your Fashion Brand is Live!',
          html: `
            <div class="text-center">
              <p class="text-lg mb-3">${data.message}</p>
              <div class="p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border-2 border-orange-200 mb-3">
                <p class="text-sm text-gray-600 mb-1">Your store is now live at:</p>
                <p class="text-xl font-bold text-orange-600">
                  https://${formData.username}.vasstr.com
                </p>
              </div>
              <p class="text-sm text-gray-500">Login to start adding your designs!</p>
            </div>
          `,
          confirmButtonText: 'Go to Login',
          confirmButtonColor: '#d25614',
          width: '32em'
        }).then(() => {
          window.location.href = '/login';
        });
      } else {
        setErrorMessage(data.error);
      }
    } catch (error) {
      devLog.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const features = [
    {
      icon: <Store className="w-5 h-5" />,
      title: "Your Own Store",
      description: "yourname.vasstr.com"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      title: "Live in 5 Minutes",
      description: "Start selling instantly"
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "100% Free to Start",
      description: "No upfront costs"
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      title: "Grow Your Brand",
      description: "Full control & ownership"
    }
  ];

  const steps = [
    { number: 1, title: "Brand Details", active: currentSection >= 1 },
    { number: 2, title: "Security", active: currentSection >= 2 }
  ];

  return (
    <div className="min-h-screen bg-white font-fell relative overflow-hidden">
      <UniversalNavbar />

      <div className="relative pt-20 sm:pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Clean Hero Section */}
          <motion.div
            className="text-center mb-8 sm:mb-12"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Simple Badge */}
            <motion.div
              className="inline-flex items-center px-4 py-2 bg-white border-2 border-gray-200 rounded-full mb-4 sm:mb-6"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: COLORS.AUTUMN }}></span>
              <span className="text-gray-700 font-semibold text-xs sm:text-sm">Launch Your Independent Fashion Brand</span>
            </motion.div>

            {/* Main Heading - Clean Typography */}
            <motion.h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              Create Your Fashion Brand
              <br />
              <span className="text-gray-600">Website in Minutes</span>
            </motion.h1>

            {/* Subheading */}
            <motion.p
              className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 px-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              Get your own branded store at{' '}
              <span className="font-bold" style={{ color: COLORS.AUTUMN }}>yourname.vasstr.com</span>
              {' '}— Manage catalog, orders & customers independently
            </motion.p>

            {/* Features Grid - Clean Design with White Backgrounds */}
            <motion.div
              className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto mb-8 sm:mb-12"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  className="group relative bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 border-2 border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center text-gray-700 mb-2 sm:mb-3 mx-auto group-hover:border-gray-300 transition-colors duration-300">
                    {feature.icon}
                  </div>
                  <h4 className="font-bold text-gray-900 text-xs sm:text-sm mb-1">{feature.title}</h4>
                  <p className="text-[10px] sm:text-xs text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Progress Steps - Clean Design */}
          <motion.div
            className="mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <div className="max-w-3xl mx-auto px-2 sm:px-4">
              <div className="flex justify-between items-center relative">
                {/* Progress Line Background */}
                <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 -z-10 mx-8 sm:mx-12"></div>
                {/* Active Progress Line - Orange only */}
                <motion.div
                  className="absolute top-5 left-0 h-1 -z-10 mx-8 sm:mx-12"
                  style={{ backgroundColor: COLORS.AUTUMN }}
                  initial={{ width: '0%' }}
                  animate={{ width: `${((currentSection - 1) / (steps.length - 1)) * 100}%` }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                />

                {steps.map((step, index) => (
                  <div key={index} className="flex flex-col items-center flex-1">
                    <motion.div
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-sm sm:text-base mb-2 transition-all duration-300 ${
                        step.active
                          ? 'text-white shadow-lg scale-110'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                      style={step.active ? { backgroundColor: COLORS.AUTUMN } : {}}
                      whileHover={{ scale: step.active ? 1.15 : 1 }}
                    >
                      {step.active ? <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" /> : step.number}
                    </motion.div>
                    <span className={`text-[10px] sm:text-xs font-medium text-center px-1 ${step.active ? 'font-semibold' : 'text-gray-500'}`} style={step.active ? { color: COLORS.AUTUMN } : {}}>
                      {step.title}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Registration Form Card - Clean White */}
          <motion.div
            className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border-2 border-gray-200 overflow-hidden"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="p-6 sm:p-8 lg:p-12">
              {errorMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <Alert variant="error" icon>
                    {errorMessage}
                  </Alert>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8 sm:space-y-10">
                {/* Section 1: Personal Information */}
                <motion.div
                  variants={itemVariants}
                  onFocus={() => setCurrentSection(1)}
                  className="scroll-mt-24"
                >
                  <div className="flex items-center mb-6">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-md" style={{ backgroundColor: COLORS.AUTUMN }}>
                      <span className="text-white font-bold text-base sm:text-lg">1</span>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">Brand Owner Details</h3>
                      <p className="text-xs sm:text-sm text-gray-500">Create your unique brand identity</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Username with Live Preview - Clean Design */}
                    <div>
                      <Input
                        type="text"
                        name="username"
                        id="username"
                        label="Choose Your Brand Username"
                        placeholder="e.g., mybrand, fashionstore"
                        value={formData.username}
                        onChange={handleChange}
                        icon={<User size={18} />}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        maxLength={30}
                        error={fieldErrors.username}
                        required
                      />
                      <AnimatePresence>
                        {formData.username && (
                          <motion.div
                            initial={{ opacity: 0, y: -10, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -10, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-3 overflow-hidden"
                          >
                            <div className="relative p-4 sm:p-5 bg-white rounded-xl sm:rounded-2xl border-2 border-gray-200">
                              <div className="relative">
                                <div className="flex items-center mb-2">
                                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" style={{ color: COLORS.AUTUMN }} />
                                  <p className="text-xs sm:text-sm font-medium text-gray-700">Your store will be live at:</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 sm:p-4 border-2 border-gray-200">
                                  <p className="text-base sm:text-lg md:text-xl font-bold break-all" style={{ color: COLORS.AUTUMN }}>
                                    https://{formData.username}.vasstr.com
                                  </p>
                                </div>
                                <p className="text-[10px] sm:text-xs text-gray-600 mt-2 flex items-center">
                                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 mr-1" />
                                  Share this URL with your customers!
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Contact and Email - Responsive Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <Input
                        type="tel"
                        name="contact_number"
                        id="contact_number"
                        label="Contact Number"
                        placeholder="10-digit mobile number"
                        value={formData.contact_number}
                        onChange={handleChange}
                        icon={<Phone size={18} />}
                        autoComplete="tel"
                        inputMode="numeric"
                        maxLength={10}
                        error={fieldErrors.contact_number}
                        required
                      />
                      <Input
                        type="email"
                        name="email"
                        id="email"
                        label="Email Address"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        icon={<Mail size={18} />}
                        autoComplete="email"
                        error={fieldErrors.email}
                        required
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Section 2: Security Information */}
                <motion.div
                  variants={itemVariants}
                  onFocus={() => setCurrentSection(2)}
                  className="scroll-mt-24"
                >
                  <div className="flex items-center mb-6">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-700 rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-md">
                      <span className="text-white font-bold text-base sm:text-lg">2</span>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">Security Information</h3>
                      <p className="text-xs sm:text-sm text-gray-500">Secure your brand account</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Password Fields - Responsive Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        id="password"
                        label="Create Password"
                        placeholder="Minimum 8 characters"
                        value={formData.password}
                        onChange={handleChange}
                        icon={<Lock size={18} />}
                        autoComplete="new-password"
                        error={fieldErrors.password}
                        iconRight={
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="text-gray-500 hover:text-gray-700 transition-colors p-1"
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        }
                        required
                      />
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirm_password"
                        id="confirm_password"
                        label="Confirm Password"
                        placeholder="Re-enter password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        icon={<Lock size={18} />}
                        autoComplete="new-password"
                        error={fieldErrors.confirm_password}
                        iconRight={
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="text-gray-500 hover:text-gray-700 transition-colors p-1"
                          >
                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        }
                        required
                      />
                    </div>

                    {/* Terms and Conditions - Clean White Design */}
                    <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 border-2 border-gray-200">
                      <div className="flex items-start">
                        <div className="flex items-center h-5 mt-0.5">
                          <input
                            type="checkbox"
                            id="termsAccepted"
                            name="termsAccepted"
                            checked={formData.termsAccepted}
                            onChange={handleChange}
                            required
                            className="w-5 h-5 border-gray-300 rounded focus:ring-2 cursor-pointer"
                            style={{ accentColor: COLORS.AUTUMN }}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="termsAccepted" className="font-medium text-gray-700 cursor-pointer">
                            I agree to the{' '}
                            <a
                              href="/TermsNConditions"
                              className="hover:underline font-semibold transition-colors duration-300"
                              style={{ color: COLORS.AUTUMN }}
                            >
                              terms and conditions
                            </a>
                          </label>
                          <p className="text-xs text-gray-500 mt-1">By signing up, you create your independent fashion brand website</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Submit Section */}
                <motion.div variants={itemVariants} className="space-y-6 pt-4">
                  {/* Trust Indicators - Pure White with Borders */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
                    <div className="text-center p-2 sm:p-3 bg-white rounded-lg sm:rounded-xl border-2 border-gray-200">
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mx-auto mb-1" />
                      <p className="text-[10px] sm:text-xs font-semibold text-gray-700">Free to Start</p>
                    </div>
                    <div className="text-center p-2 sm:p-3 bg-white rounded-lg sm:rounded-xl border-2 border-gray-200">
                      <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mx-auto mb-1" />
                      <p className="text-[10px] sm:text-xs font-semibold text-gray-700">Secure Setup</p>
                    </div>
                    <div className="text-center p-2 sm:p-3 bg-white rounded-lg sm:rounded-xl border-2 border-gray-200">
                      <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mx-auto mb-1" />
                      <p className="text-[10px] sm:text-xs font-semibold text-gray-700">Live in 5 Min</p>
                    </div>
                  </div>

                  {/* Submit Button - ONLY place with autumn color */}
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={isLoading}
                    icon={<Sparkles size={20} />}
                    disabled={isLoading}
                    className="text-base sm:text-lg py-4 sm:py-5 shadow-xl hover:shadow-2xl"
                  >
                    {isLoading ? 'Launching Your Brand...' : 'Launch Your Fashion Brand Now'}
                  </Button>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-3 sm:px-4 bg-white text-gray-500">Already have an account?</span>
                    </div>
                  </div>

                  {/* Login Link */}
                  <div className="text-center">
                    <a
                      href="/login"
                      className="inline-flex items-center justify-center font-semibold hover:underline transition-colors duration-300 text-base sm:text-lg group"
                      style={{ color: COLORS.AUTUMN }}
                    >
                      Sign in to your account
                      <motion.span
                        className="ml-2"
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        →
                      </motion.span>
                    </a>
                  </div>

                  {/* Contact Support */}
                  <div className="text-center pt-4 border-t border-gray-200">
                    <p className="text-xs sm:text-sm text-gray-600 mb-2">Need help? Contact our support team</p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center text-xs sm:text-sm">
                      <a
                        href="mailto:team@vasstr.com"
                        className="hover:underline font-medium transition-colors"
                        style={{ color: COLORS.AUTUMN }}
                      >
                        team@vasstr.com
                      </a>
                      <span className="hidden sm:inline text-gray-400">•</span>
                      <span className="text-gray-600">+91 97043 87878</span>
                    </div>
                  </div>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      <VFooter />
    </div>
  );
};

export default RegistrationForm;